import React, { useEffect, useMemo, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Save, Search, Pencil, X, Lock, FileText } from "lucide-react";
import { toast } from "sonner";
import ExportPreview from "@/components/ExportPreview";

const FIELDS = [
  ["gaji_per_hari", "Gaji/Hari (Proyek)"],
  ["gaji_non_proyek_per_hari", "Gaji/Hari (Non-Proyek)"],
  ["uang_makan_per_hari", "Makan/Hari"],
  ["uang_luar_kota_per_hari", "Luar Kota/Hari"],
  ["tarif_lembur_per_jam", "Lembur/Jam (auto ÷8)"],
  ["tunjangan_jabatan", "Tj Jabatan"],
  ["tunjangan_skill", "Tj Skill"],
  ["tunjangan_tetap", "Tj Tetap"],
  ["tarif_lainnya", "Lainnya"],
];

export default function SalaryRates() {
  const { user } = useAuth();
  const canWrite = ["super_admin", "hr", "direktur"].includes(user.role);
  const [employees, setEmployees] = useState([]);
  const [data, setData] = useState({});
  const [hasRate, setHasRate] = useState({});
  const [checked, setChecked] = useState({});
  const [q, setQ] = useState("");
  const [pdf, setPdf] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const cancelEdit = () => { setEditMode(false); load(); };
  const startEdit = () => { setChecked({}); setEditMode(true); };

  const load = async () => {
    const [er, rr] = await Promise.all([api.get("/employees"), api.get("/salary-rates")]);
    const emps = er.data.filter((e) => e.aktif);
    const activeMap = {};
    rr.data.forEach((r) => { if (r.status === "Aktif" && !activeMap[r.employee_id]) activeMap[r.employee_id] = r; });
    const d = {}, hr = {};
    emps.forEach((e) => {
      const a = activeMap[e.id];
      hr[e.id] = !!a;
      d[e.id] = Object.fromEntries(FIELDS.map(([k]) => [k, a ? (a[k] || 0) : 0]));
    });
    setEmployees(emps); setData(d); setHasRate(hr); setChecked({});
  };
  useEffect(() => { load(); }, []);

  const setVal = (id, k, v) => setData((s) => {
    const row = { ...s[id], [k]: v };
    if (k === "gaji_per_hari") row.tarif_lembur_per_jam = Math.round((Number(v) || 0) / 8);
    return { ...s, [id]: row };
  });
  const toggle = (id) => setChecked((c) => ({ ...c, [id]: !c[id] }));

  const filtered = useMemo(() => {
    const t = q.toLowerCase();
    return employees.filter((e) => !t || e.nama.toLowerCase().includes(t) ||
      (e.kode_pegawai || "").toLowerCase().includes(t) || (e.jabatan || "").toLowerCase().includes(t));
  }, [employees, q]);

  const allChecked = filtered.length > 0 && filtered.every((e) => checked[e.id]);
  const toggleAll = () => {
    const v = !allChecked;
    setChecked((c) => { const n = { ...c }; filtered.forEach((e) => { n[e.id] = v; }); return n; });
  };

  const selectedCount = employees.filter((e) => checked[e.id]).length;

  const save = async () => {
    const rows = employees.filter((e) => checked[e.id]).map((e) => {
      const base = Object.fromEntries(FIELDS.map(([k]) => [k, Number(data[e.id]?.[k] || 0)]));
      base.tarif_lembur_per_jam = Math.round((base.gaji_per_hari || 0) / 8);
      return { employee_id: e.id, ...base };
    });
    if (!rows.length) return toast.error("Centang minimal satu pegawai untuk disimpan");
    setSaving(true);
    try {
      const r = await api.post("/salary-rates/bulk", { rows });
      toast.success(`${r.data.saved} tarif gaji tersimpan`, { id: "rate-save" });
      setEditMode(false);
      load();
    } catch (e) { toast.error(apiErr(e)); }
    setSaving(false);
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Master Gaji" subtitle="Rekap tarif gaji seluruh pegawai — terkunci, klik Ubah / Edit untuk mengubah" />
      <Card className="p-4 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input className="pl-9" placeholder="Cari nama / kode / jabatan" value={q} data-testid="rate-search" onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="flex items-center gap-3 text-sm text-slate-500">
          {editMode ? <span className="rounded bg-amber-100 px-2 py-1 text-xs font-bold text-amber-800" data-testid="rate-edit-mode">MODE UBAH</span>
            : <span className="flex items-center gap-1 text-xs text-slate-500" data-testid="rate-locked"><Lock className="h-3.5 w-3.5" /> Terkunci</span>}
          {selectedCount > 0 && <span data-testid="rate-selected-count">{selectedCount} dipilih</span>}
          {!editMode && selectedCount > 0 && <button className="text-xs text-slate-500 underline" onClick={() => setChecked({})} data-testid="rate-clear-selection">hapus centang</button>}
          {!editMode && <Button variant="outline" className="border-orange-300 text-orange-700 hover:bg-orange-50" data-testid="rate-pdf-btn"
            onClick={() => {
              const ids = selectedCount ? employees.filter((e) => checked[e.id]).map((e) => e.id) : filtered.map((e) => e.id);
              setPdf({ url: `/reports/salary-master/pdf?ids=${ids.join(",")}`, title: `Master Gaji (${ids.length} karyawan)`, file: "master_gaji.pdf" });
            }}>
            <FileText className="h-4 w-4 mr-1" /> {selectedCount ? `Preview PDF (${selectedCount} dicentang)` : "Preview PDF (semua)"}</Button>}
          {canWrite && !editMode && <Button variant="outline" className="border-blue-300 text-blue-800" onClick={startEdit} data-testid="rate-edit-btn"><Pencil className="h-4 w-4 mr-1" /> Ubah / Edit</Button>}
          {canWrite && editMode && <Button variant="outline" onClick={cancelEdit} data-testid="rate-cancel-btn"><X className="h-4 w-4 mr-1" /> Batal</Button>}
          {canWrite && editMode && <Button className="bg-blue-800 hover:bg-blue-900" disabled={saving} onClick={save} data-testid="rate-save-btn"><Save className="h-4 w-4 mr-1" /> Simpan Terpilih</Button>}
        </div>
      </Card>

      <div className="overflow-auto scroll-thin max-h-[70vh] rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="rate-table">
          <thead><tr className="border-b text-xs font-bold uppercase tracking-wider text-slate-500">
            <th className="sticky top-0 z-10 bg-slate-100 px-3 py-3 w-12 text-center"><input type="checkbox" checked={allChecked} onChange={toggleAll} data-testid="rate-check-all" /></th>
            <th className="sticky top-0 z-10 bg-slate-100 px-3 py-3 text-left min-w-[200px]">Pegawai</th>
            {FIELDS.map(([k, l]) => <th key={k} className="sticky top-0 z-10 bg-slate-100 px-2 py-3 text-right whitespace-nowrap w-32">{l}</th>)}
          </tr></thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={FIELDS.length + 2} className="text-center text-slate-400 py-8">Tidak ada pegawai.</td></tr>
            ) : filtered.map((e, i) => (
              <tr key={e.id} className={`border-b border-slate-100 hover:bg-slate-50 ${checked[e.id] ? "bg-blue-50/50" : ""}`} data-testid={`rate-row-${i}`}>
                <td className="px-3 py-2 text-center"><input type="checkbox" checked={!!checked[e.id]} onChange={() => toggle(e.id)} data-testid={`rate-check-${i}`} /></td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <div className="flex items-center gap-1">
                    <div className="font-medium text-slate-800">{e.nama}</div>
                    <button className="text-orange-600 hover:text-orange-700" title="Preview PDF" data-testid={`rate-pdf-${i}`}
                      onClick={() => setPdf({ url: `/reports/salary-master/pdf?ids=${e.id}`, title: `Master Gaji — ${e.nama}`, file: `master_gaji_${e.nama}.pdf` })}><FileText className="h-3.5 w-3.5" /></button>
                  </div>
                  <div className="text-xs text-slate-400">{e.kode_pegawai} · {e.jabatan || "-"}
                    {!hasRate[e.id] && <span className="ml-1 rounded bg-amber-100 px-1 py-0.5 text-[10px] font-bold text-amber-700">BELUM DIISI</span>}
                  </div>
                </td>
                {FIELDS.map(([k]) => (
                  <td key={k} className="px-2 py-2 w-32">
                    <Input type="number" className="h-9 w-full text-right" disabled={!editMode || !canWrite || k === "tarif_lembur_per_jam"}
                      value={k === "tarif_lembur_per_jam" ? Math.round((Number(data[e.id]?.gaji_per_hari) || 0) / 8) : (data[e.id]?.[k] ?? 0)}
                      data-testid={`rate-${k}-${i}`} onChange={(ev) => { setVal(e.id, k, ev.target.value); setChecked((c) => ({ ...c, [e.id]: true })); }} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-400">Tabel terkunci agar tidak salah klik. Centang karyawan lalu klik <b>Preview PDF</b> untuk mencetak yang dicentang saja (tanpa centang = semua). Untuk mengubah tarif klik <b>Ubah / Edit</b>, ubah tarif (baris otomatis tercentang), lalu <b>Simpan Terpilih</b> — atau <b>Batal</b> untuk membuang perubahan. Menyimpan membuat tarif aktif baru & tarif lama disimpan sebagai histori.</p>
      <ExportPreview open={!!pdf} onClose={() => setPdf(null)} title={pdf?.title} pdfUrl={pdf?.url}
        items={pdf ? [{ label: "Unduh PDF", url: pdf.url, filename: pdf.file }] : []} />
    </div>
  );
}
